import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { apiGet, apiPost } from '../lib/api.js';
import { useAuth } from '../store/auth.js';
import SmsTwoFactorSection from '../components/security/SmsTwoFactorSection.jsx';

// Security settings page. Lives at /settings/security.
// Lets the user enable TOTP-based 2FA, view & download backup codes,
// regenerate backup codes, and disable 2FA (password re-auth required).
export default function Security() {
  const { agent, setSession } = useAuth();
  const [me, setMe] = useState(agent);
  const [stage, setStage] = useState('idle'); // idle | setup | verifying
  const [setup, setSetup] = useState(null);   // { secret, otpauthUrl, qrPngDataUrl, backupCodes }
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [disablePwd, setDisablePwd] = useState('');
  const [regenPwd, setRegenPwd] = useState('');
  const [newBackupCodes, setNewBackupCodes] = useState(null);

  useEffect(() => {
    apiGet('/auth/me').then((r) => { if (r?.agent) setMe(r.agent); }).catch(() => {});
  }, []);

  async function beginSetup() {
    setBusy(true);
    try {
      const r = await apiPost('/auth/2fa/setup', {});
      setSetup(r);
      setStage('setup');
    } catch (e) {
      toast.error(e.message || 'Failed to start 2FA setup');
    } finally {
      setBusy(false);
    }
  }

  async function verifySetup(e) {
    e.preventDefault();
    if (!setup || !/^\d{6}$/.test(code)) {
      toast.error('Enter the 6-digit code from your authenticator app.');
      return;
    }
    setBusy(true);
    try {
      await apiPost('/auth/2fa/verify-setup', {
        code,
        secret: setup.secret,
        backupCodes: setup.backupCodes,
      });
      toast.success('2FA enabled');
      setStage('idle');
      setSetup(null);
      setCode('');
      const r = await apiGet('/auth/me');
      if (r?.agent) setMe(r.agent);
    } catch (e) {
      toast.error(e.message || 'Failed to verify code');
    } finally {
      setBusy(false);
    }
  }

  async function disable() {
    if (!disablePwd) { toast.error('Enter your password'); return; }
    if (!confirm('Disable 2FA? You will only have a password protecting your account.')) return;
    setBusy(true);
    try {
      await apiPost('/auth/2fa/disable', { password: disablePwd });
      toast.success('2FA disabled');
      setDisablePwd('');
      const r = await apiGet('/auth/me');
      if (r?.agent) setMe(r.agent);
    } catch (e) {
      toast.error(e.message || 'Failed to disable 2FA');
    } finally {
      setBusy(false);
    }
  }

  async function regen() {
    if (!regenPwd) { toast.error('Enter your password'); return; }
    setBusy(true);
    try {
      const r = await apiPost('/auth/2fa/regenerate-backup-codes', { password: regenPwd });
      setNewBackupCodes(r.backupCodes);
      setRegenPwd('');
      toast.success('New backup codes generated — save them!');
    } catch (e) {
      toast.error(e.message || 'Failed to regenerate codes');
    } finally {
      setBusy(false);
    }
  }

  const mfaEnabled = !!me?.mfaEnabled;

  return (
    <div className="max-w-2xl mx-auto p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-display font-bold mb-1">Security</h1>
        <p className="text-white/60 text-sm">Two-factor authentication adds a second code to your login. Required for paying agents handling lead PII.</p>
      </div>

      <SmsTwoFactorSection />

      <div className="panel p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="font-semibold">Authenticator app (TOTP) two-factor</div>
            <div className="text-sm text-white/50">{mfaEnabled ? 'Enabled' : 'Not enabled'}</div>
          </div>
          {!mfaEnabled && stage === 'idle' && (
            <button className="btn btn-primary" onClick={beginSetup} disabled={busy}>Enable 2FA</button>
          )}
        </div>

        {stage === 'setup' && setup && (
          <div className="space-y-4 border-t border-civix-border pt-4">
            <div className="text-sm">
              1. Open your authenticator app (1Password, Authy, Google Authenticator).
              <br />2. Scan the QR code below, or enter the secret manually.
              <br />3. Enter the 6-digit code your app shows to confirm.
            </div>
            <img src={setup.qrPngDataUrl} alt="2FA QR" className="bg-white p-2 rounded-md w-44 h-44" />
            <div className="text-xs text-white/50">
              Secret: <code className="font-mono">{setup.secret}</code>
            </div>
            <div className="border border-yellow-500/40 bg-yellow-500/5 rounded-md p-3 text-sm">
              <div className="font-semibold mb-2">Backup codes — save these now</div>
              <div className="text-xs text-white/70 mb-2">Each is single-use. Use one if you lose your authenticator device.</div>
              <div className="grid grid-cols-2 gap-1 font-mono text-xs">
                {setup.backupCodes.map((c) => <div key={c}>{c}</div>)}
              </div>
            </div>
            <form onSubmit={verifySetup} className="flex gap-2 items-end">
              <div className="flex-1">
                <label className="label">6-digit code</label>
                <input className="input mt-1 font-mono tracking-widest" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="123456" />
              </div>
              <button className="btn btn-primary" type="submit" disabled={busy}>Verify & enable</button>
              <button className="btn" type="button" onClick={() => { setStage('idle'); setSetup(null); setCode(''); }}>Cancel</button>
            </form>
          </div>
        )}

        {mfaEnabled && (
          <div className="space-y-6 border-t border-civix-border pt-4">
            <div>
              <div className="font-semibold mb-2">Regenerate backup codes</div>
              <div className="text-sm text-white/60 mb-2">Old codes are invalidated. Save the new ones somewhere safe.</div>
              <div className="flex gap-2 items-end">
                <input className="input flex-1" type="password" placeholder="Confirm your password" value={regenPwd} onChange={(e) => setRegenPwd(e.target.value)} />
                <button className="btn" onClick={regen} disabled={busy}>Regenerate</button>
              </div>
              {newBackupCodes && (
                <div className="mt-3 border border-yellow-500/40 bg-yellow-500/5 rounded-md p-3">
                  <div className="font-semibold mb-2 text-sm">New backup codes — save these now</div>
                  <div className="grid grid-cols-2 gap-1 font-mono text-xs">
                    {newBackupCodes.map((c) => <div key={c}>{c}</div>)}
                  </div>
                  <button className="btn btn-sm mt-2" onClick={() => setNewBackupCodes(null)}>Got them</button>
                </div>
              )}
            </div>

            <div>
              <div className="font-semibold mb-2 text-red-300">Disable 2FA</div>
              <div className="flex gap-2 items-end">
                <input className="input flex-1" type="password" placeholder="Confirm your password" value={disablePwd} onChange={(e) => setDisablePwd(e.target.value)} />
                <button className="btn btn-danger" onClick={disable} disabled={busy}>Disable</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
